import React from "react";
import classnames from "classnames";

import { TableHeaderProps } from "../../Table";

import { SortOrder, SortOptions } from "../";

export const createTableLoadingFiller = (headers: ReadonlyArray<TableHeaderProps>, rows = 10): ReadonlyArray<TableHeaderProps> => {
  const tableFillerRow = headers.reduce((row, { key = "*" }) => {
    return { ...row, [key]: "*****" };
  }, {});
  return [...Array(rows).fill(tableFillerRow)];
};


const keyUpHandler = (e: React.KeyboardEvent<HTMLElement>, cb: Function): void => {
  // When key pressed is "Return"
  if (e.keyCode === 13) cb();
};

export const formatHeaders = (
  headers: ReadonlyArray<TableHeaderProps>,
  sortByColumns: ReadonlyArray<SortOptions>,

  handleSort: (sortOptions: Array<SortOptions>) => void,

  onClickSort: Function,
): ReadonlyArray<TableHeaderProps> => {
  /* DataTable currently supports only one level of sort. But the order_by
     modifier array in the backend supports an arbitrary numbers of SortOptions.
     This could be expanded upon in the future. For now, this component will
     only deal with the first SortOptions item in the array. */
  let primarySortedColumnName = "";
  let primarySortedColumnDirection = SortOrder.Asc;
  if (sortByColumns && sortByColumns.length > 0)
    [primarySortedColumnName, primarySortedColumnDirection] = Object.entries(sortByColumns[0])[0];

  const sortClick = (key: string, direction: string): void => {
    if (onClickSort) onClickSort(key, direction);
  };

  return headers.map(
    (header: TableHeaderProps): TableHeaderProps => {
      const { key = "", isSortable = true } = header;
      const columnIsSortedOn = key === primarySortedColumnName;
      let additionalDOMAttributes = {};

      // Inject sort functionality into header label
      let complexLabel: JSX.Element | string = "";
      if (header.label) {
        if (isSortable) {
          const isActiveForUp = columnIsSortedOn && primarySortedColumnDirection === SortOrder.Asc;
          const isActiveForDown = columnIsSortedOn && primarySortedColumnDirection === SortOrder.Desc;
          const upButtonClassList = classnames("header-sort-button-up", { "sort-icon-active": isActiveForUp });
          const downButtonClassList = classnames("header-sort-button-down", { "sort-icon-active": isActiveForDown });

          const toggleSort = (): void => {
            // When clicking an unsorted-on column, default direction to ascending
            let direction = SortOrder.Asc;
            if (isActiveForUp) {
              direction = SortOrder.Desc;
            }
            sortClick(key, direction);
            handleSort([{ [key]: direction }]);
          };

          const buttonElements = [
            {
              id: `sort-ascending-by-${key}`,
              classList: upButtonClassList,
              sortFunction: (): void => handleSort([{ [key]: SortOrder.Asc }]),
            },
            {
              id: `sort-descending-by-${key}`,
              classList: downButtonClassList,
              sortFunction: (): void => handleSort([{ [key]: SortOrder.Desc }]),
            },
          ].map(
            ({ id, classList, sortFunction }: { id: string; classList?: string; sortFunction: () => void }): JSX.Element => (
              <span
                key={id}
                id={id}
                className={classList}
                role="button"
                onClick={(e): void => {
                  e.stopPropagation();
                  sortFunction();
                }}
                onKeyUp={(e: React.KeyboardEvent<HTMLElement>): void => keyUpHandler(e, sortFunction)}
              />
            ),
          );

          complexLabel = (
            <span className={"header-item"}>
              <span>{header.label}</span>
              <span className="header-sort-buttons-container">{buttonElements}</span>
            </span>
          );

          additionalDOMAttributes = {
            tabIndex: "0",
            role: "button",
            onClick: toggleSort,
            onKeyUp: (e: React.KeyboardEvent<HTMLElement>): void => keyUpHandler(e, toggleSort),
          };
        } else {
          complexLabel = (
            <span className={"header-item"}>
              <span>{header.label}</span>
            </span>
          );
        }
      }

      return {
        ...header,
        columnIsSortedOn,
        sortedOnCellClass: "sorted-on",
        additionalDOMAttributes,
        className: classnames(header.className, { sortable: header.label && isSortable }, { "sorted-on": columnIsSortedOn }),
        label: complexLabel,
      };
    },
  );
};
